import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useAuth } from "../../../AuthContext";
import {
  FaUserTie,
  FaEdit,
  FaTrash,
  FaPlus,
  FaFileExcel,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const API_BASE_URL = "https://sapl.cantarellisolucoes.com.br/wp-json/custom/v1";

const Parlamentares = () => {
  const { user } = useAuth();
  const toast = useRef(null);

  // Estados
  const [loading, setLoading] = useState(false);
  const [parlamentares, setParlamentares] = useState([]);
  const [legislaturas, setLegislaturas] = useState([]);
  const [legislaturaSelecionada, setLegislaturaSelecionada] = useState(null);

  // Estados de filtro
  const [termoPesquisa, setTermoPesquisa] = useState("");
  const [apenasAtivos, setApenasAtivos] = useState(false);
  const [apenasTitulares, setApenasTitulares] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Estados do modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editandoParlamentar, setEditandoParlamentar] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    nome_parlamentar: "",
    cpf: "",
    partido: "",
    email: "",
    telefone: "",
    ativo: true,
    titular: true,
    legislatura_id: null,
    data_inicio_mandato: null,
    data_fim_mandato: null,
  });
  const [submitting, setSubmitting] = useState(false);

  // Função genérica para requisições fetch com token
  const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${user?.token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
        ...options.headers,
      },
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw { status: response.status, data };
    }

    return data;
  };

  // Carregar legislaturas da API
  const carregarLegislaturas = useCallback(async () => {
    try {
      const response = await apiRequest("/legislaturas");
      const legislaturasList = response.data.map((leg) => ({
        id: leg.id,
        name: leg.descricao,
        code: leg.numero,
        atual: leg.ativa === 1,
        data_inicio: leg.data_inicio,
        data_fim: leg.data_fim,
      }));
      setLegislaturas(legislaturasList);

      // Seleciona a legislatura atual por padrão
      const atual = legislaturasList.find((l) => l.atual);
      if (atual) setLegislaturaSelecionada(atual);
      else if (legislaturasList[0])
        setLegislaturaSelecionada(legislaturasList[0]);
    } catch (error) {
      console.error("Erro ao carregar legislaturas:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar as legislaturas",
        life: 5000,
      });
    }
  }, [user?.token]);

  // Carregar parlamentares (com filtro por legislatura)
  const carregarParlamentares = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/parlamentares";
      const params = new URLSearchParams();

      if (apenasAtivos) params.append("ativo", "true");
      if (apenasTitulares) params.append("titular", "true");
      if (termoPesquisa) params.append("search", termoPesquisa);
      if (legislaturaSelecionada?.id)
        params.append("legislatura_id", legislaturaSelecionada.id);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await apiRequest(url);
      setParlamentares(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar parlamentares:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar os parlamentares",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [
    user?.token,
    apenasAtivos,
    apenasTitulares,
    termoPesquisa,
    legislaturaSelecionada,
  ]);

  // Efeitos de carregamento inicial
  useEffect(() => {
    if (user?.token) {
      carregarLegislaturas();
    }
  }, [user?.token, carregarLegislaturas]);

  useEffect(() => {
    if (user?.token && legislaturaSelecionada) {
      carregarParlamentares();
    }
  }, [user?.token, carregarParlamentares, legislaturaSelecionada]);

  // Abrir modal para criar novo parlamentar
  const abrirModalCriar = () => {
    setEditandoParlamentar(null);
    setFormData({
      nome: "",
      nome_parlamentar: "",
      cpf: "",
      partido: "",
      email: "",
      telefone: "",
      ativo: true,
      titular: true,
      legislatura_id: legislaturaSelecionada?.id || null,
      data_inicio_mandato: legislaturaSelecionada?.data_inicio
        ? new Date(legislaturaSelecionada.data_inicio)
        : null,
      data_fim_mandato: legislaturaSelecionada?.data_fim
        ? new Date(legislaturaSelecionada.data_fim)
        : null,
    });
    setModalVisible(true);
  };

  // Abrir modal para editar parlamentar
  const abrirModalEditar = (parlamentar) => {
    
    setEditandoParlamentar(parlamentar);
    setFormData({
        nome: parlamentar.nome || "",
        nome_parlamentar: parlamentar.nome_parlamentar || "",
        cpf: parlamentar.cpf || "",
        partido: parlamentar.partido || "",
        email: parlamentar.email || "",
        telefone: parlamentar.telefone || "",
        // CONVERSÃO ROBUSTA - Aceita 1, "1", true, "true"
        ativo: parlamentar.ativo === 1 || parlamentar.ativo === "1" || parlamentar.ativo === true || parlamentar.ativo === "true",
        titular: parlamentar.titular === 1 || parlamentar.titular === "1" || parlamentar.titular === true || parlamentar.titular === "true",
        legislatura_id: parlamentar.legislatura_id || legislaturaSelecionada?.id || null,
        data_inicio_mandato: parlamentar.data_inicio_mandato ? new Date(parlamentar.data_inicio_mandato) : null,
        data_fim_mandato: parlamentar.data_fim_mandato ? new Date(parlamentar.data_fim_mandato) : null,
    });
    setModalVisible(true);
};

  // Salvar parlamentar (criar ou editar)
  const salvarParlamentar = async () => {
    // Validações
    if (!formData.nome.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "O nome do parlamentar é obrigatório",
        life: 3000,
      });
      return;
    }

    if (!formData.cpf.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "O CPF é obrigatório",
        life: 3000,
      });
      return;
    }

    if (!formData.legislatura_id) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "Selecione uma legislatura",
        life: 3000,
      });
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        nome: formData.nome,
        nome_parlamentar: formData.nome_parlamentar,
        cpf: formData.cpf,
        partido: formData.partido,
        email: formData.email,
        telefone: formData.telefone,
        ativo: formData.ativo,
        titular: formData.titular,
        legislatura_id: formData.legislatura_id,
        data_inicio_mandato: formData.data_inicio_mandato
          ? formData.data_inicio_mandato.toISOString().split("T")[0]
          : null,
        data_fim_mandato: formData.data_fim_mandato
          ? formData.data_fim_mandato.toISOString().split("T")[0]
          : null,
      };

      if (editandoParlamentar) {
        await apiRequest(`/parlamentares/${editandoParlamentar.id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Parlamentar atualizado com sucesso",
          life: 3000,
        });
      } else {
        await apiRequest("/parlamentares", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Parlamentar criado com sucesso",
          life: 3000,
        });
      }

      setModalVisible(false);
      carregarParlamentares();
    } catch (error) {
      console.error("Erro ao salvar parlamentar:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: error.data?.message || "Falha ao salvar parlamentar",
        life: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Inativar/Ativar parlamentar
  const confirmarInativacao = (parlamentar) => {
    const acao = parlamentar.ativo ? "inativar" : "ativar";

    confirmDialog({
      header: "",
      style: { width: "400px" },
      className: "custom-confirm-dialog",

      message: (
        <div className="flex flex-column align-items-center text-center">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "3rem", color: "#f59e0b", marginBottom: "1rem" }}
          ></i>

          <span
            style={{
              fontSize: "1.1rem",
              fontWeight: "500",
              marginBottom: "0.5rem",
            }}
          >
            Confirmar {acao}
          </span>

          <span style={{ fontSize: "0.95rem", color: "#6b7280" }}>
            Deseja realmente <strong>{acao}</strong> o parlamentar
          </span>

          <span
            style={{ fontSize: "1rem", fontWeight: "600", marginTop: "0.5rem" }}
          >
            {parlamentar.nome}?
          </span>
        </div>
      ),

      footer: (options) => (
        <div className="flex justify-content-center gap-2 w-full">
          <button
            className="p-button p-component p-button-sm p-button-secondary"
            onClick={options.reject}
          >
            <span className="p-button-label">Cancelar</span>
          </button>

          <button
            className="p-button p-component p-button-sm p-button-success"
            onClick={options.accept}
          >
            <span className="p-button-label">Confirmar</span>
          </button>
        </div>
      ),

      accept: async () => {
        setLoading(true);
        try {
          if (parlamentar.ativo) {
            await apiRequest(`/parlamentares/${parlamentar.id}/inativar`, {
              method: "POST",
            });
          } else {
            await apiRequest(`/parlamentares/${parlamentar.id}`, {
              method: "PUT",
              body: JSON.stringify({ ativo: true }),
            });
          }

          toast.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: `Parlamentar ${acao}do com sucesso`,
            life: 3000,
          });

          carregarParlamentares();
        } catch (error) {
          console.error(`Erro ao ${acao} parlamentar:`, error);

          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: error.data?.message || `Falha ao ${acao} parlamentar`,
            life: 5000,
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Templates da tabela
  const nomeBody = (rowData) => (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#3b82f6",
          fontSize: "1.2rem",
        }}
      >
        <FaUserTie />
      </div>
      <div>
        <div style={{ fontWeight: "500" }}>{rowData.nome}</div>
        {rowData.nome_parlamentar && (
          <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
            Urna: {rowData.nome_parlamentar}
          </div>
        )}
      </div>
    </div>
  );

  const partidoBody = (rowData) => {
    const isSemFiliacao =
      rowData.partido === "Não possui filiação" || !rowData.partido;
    return (
      <Tag
        value={rowData.partido || "Sem partido"}
        severity={isSemFiliacao ? "secondary" : "info"}
        icon={isSemFiliacao ? "pi pi-user" : "pi pi-flag"}
      />
    );
  };

  const legislaturaBody = (rowData) => {
    if (!rowData.legislatura_desc)
      return <span style={{ color: "#9ca3af" }}>---</span>;
    return (
      <Tag
        value={rowData.legislatura_desc}
        severity="info"
        icon="pi pi-calendar"
      />
    );
  };

  const ativoBody = (rowData) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
    <Tag
      value={rowData.ativo == 1 ? "Ativo" : "Inativo"}
      severity={rowData.ativo == 1 ? "success" : "danger"}
      icon={rowData.ativo == 1 ? "pi pi-check-circle" : "pi pi-times-circle"}
    />
  </div>
);

const statusBody = (rowData) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
    <Tag
      value={rowData.titular == 1 ? "Titular" : "Suplente"}
      severity={rowData.titular == 1 ? "info" : "warning"}
      icon={rowData.titular == 1 ? "pi pi-star" : "pi pi-user"}
      style={{ fontSize: "0.7rem" }}
    />
  </div>
);

  const mandatoBody = (rowData) => {
    if (!rowData.data_inicio_mandato)
      return <span style={{ color: "#9ca3af" }}>---</span>;
    const inicio = new Date(rowData.data_inicio_mandato).toLocaleDateString(
      "pt-BR",
    );
    const fim = rowData.data_fim_mandato
      ? new Date(rowData.data_fim_mandato).toLocaleDateString("pt-BR")
      : "atual";
    return (
      <span style={{ fontSize: "0.85rem" }}>
        {inicio} - {fim}
      </span>
    );
  };

  const acoesBody = (rowData) => {
    return (
      <React.Fragment>
        <Button
          icon="pi pi-file-edit"
          size="small"
          severity="success"
          tooltip="Editar"
          tooltipOptions={{ position: "left" }}
          className="mr-2 custom-button button-efeito"
          style={{ width: "32px", height: "32px" }}
          onClick={() => abrirModalEditar(rowData)}
        />
        <Button
          icon="pi pi-trash"
          size="small"
          severity="warning"
          tooltip="Inativar"
          tooltipOptions={{ position: "left" }}
          className="custom-button button-efeito"
          style={{ width: "32px", height: "32px" }}
          onClick={() => confirmarInativacao(rowData)}
        />
      </React.Fragment>
    );
  };

  const emptyMessage = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 20px",
        color: "#6b7280",
      }}
    >
      <FaUserTie size={48} style={{ color: "#9ca3af", marginBottom: "20px" }} />
      <h3 style={{ marginBottom: "8px", color: "#374151" }}>
        Nenhum parlamentar encontrado
      </h3>
      <p
        style={{ textAlign: "center", maxWidth: "400px", fontSize: "0.85rem" }}
      >
        Não há parlamentares para a legislatura selecionada.
      </p>
    </div>
  );

  const modalFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancelar"
        size="small"
        severity="secondary"
        icon="pi pi-times"
        onClick={() => setModalVisible(false)}
        outlined
      />
      <Button
        label="Salvar"
        size="small"
        severity="success"
        icon="pi pi-check"
        onClick={salvarParlamentar}
        loading={submitting}
        autoFocus
      />
    </div>
  );

  const totalParlamentares = parlamentares.length;

  return (
    <div className="dashboard-container">
      
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="dashboard-header">
        <h1>Parlamentares</h1>
        <p>Gestão dos parlamentares do legislativo</p>
      </div>

      <div className="page-content">
        {/* Seção de escolha da legislatura */}
        <Card style={{ padding: "0", marginBottom: "10px" }}>
          <div style={{ padding: "0 10px" }}>
            <h2 style={{ margin: 0, color: "#374151" }}>Selecione o Período</h2>
          </div>

          <div style={{ padding: "0 10px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "3fr 1fr",
                gap: "24px",
                alignItems: "end",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <label
                  htmlFor="legislatura-dropdown"
                  style={{
                    fontWeight: "500",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Escolha uma Legislatura
                </label>
                <Dropdown
                  value={legislaturaSelecionada}
                  options={legislaturas}
                  onChange={(e) => setLegislaturaSelecionada(e.value)}
                  optionLabel="name"
                  placeholder="Selecione uma legislatura"
                  loading={!legislaturas.length}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  alignItems: "flex-start",
                }}
              >
                <label
                  htmlFor="sessao-dropdown"
                  style={{
                    fontWeight: "500",
                    color: "#374151",
                    fontSize: "0.95rem",
                  }}
                >
                  Pesquisar Parlamentar
                </label>
                <Button
                  label={mostrarFiltros ? "Ocultar Filtros" : "Mostrar Filtros"}
                  icon={mostrarFiltros ? <FaChevronUp /> : <FaChevronDown />}
                  severity="secondary"
                  outlined
                  onClick={() => setMostrarFiltros(!mostrarFiltros)}
                  style={{ minWidth: "200px" }}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Filtros de busca (mostra/oculta) */}
        {mostrarFiltros && (
          <Card style={{ padding: "0", marginBottom: "10px" }}>
            <div style={{ padding: "0 10px" }}>
              <h2 style={{ margin: 0, color: "#374151" }}>Filtros</h2>
            </div>

            <div style={{ padding: "0 10px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    htmlFor="legislatura-dropdown"
                    style={{
                      fontWeight: "500",
                      color: "#374151",
                      fontSize: "0.95rem",
                    }}
                  >
                    Nome ou Partido do Parlamentar
                  </label>
                  <InputText
                    id="pesquisa-parlamentar"
                    value={termoPesquisa}
                    onChange={(e) => setTermoPesquisa(e.target.value)}
                    placeholder="Nome ou partido..."
                    className="pesquisa-input"
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    htmlFor="sessao-dropdown"
                    style={{
                      fontWeight: "500",
                      color: "#374151",
                      fontSize: "0.95rem",
                    }}
                  >
                    Filtros Avançados
                  </label>

                  <div
                    style={{
                      display: "flex",
                      gap: "24px",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Checkbox
                        inputId="apenas-ativos"
                        checked={apenasAtivos}
                        onChange={(e) => setApenasAtivos(e.checked)}
                      />
                      <label htmlFor="apenas-ativos" className="checkbox-label">
                        Apenas Ativos
                      </label>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Checkbox
                        inputId="apenas-titulares"
                        checked={apenasTitulares}
                        onChange={(e) => setApenasTitulares(e.checked)}
                      />
                      <label
                        htmlFor="apenas-titulares"
                        className="checkbox-label"
                      >
                        Apenas Titulares
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Tabela de parlamentares */}
        <Card style={{ padding: "0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              padding: "10px",
            }}
          >
            <Button
              label="Adicionar"
              icon="pi pi-plus"
              severity="success"
              size="small"
              onClick={abrirModalCriar}
              disabled={loading}
            />
            <Button
              label="Exportar"
              icon="pi pi-file-excel"
              severity="secondary"
              size="small"
              outlined
              onClick={() => console.log("Exportar...")}
            />
          </div>

          <div style={{ padding: "0 0 10px 0", width: "100%" }}>
            <Message
              severity="info"
              text={`Legislatura: ${legislaturaSelecionada?.name || "Selecione uma legislatura"} ${legislaturaSelecionada?.atual ? "(Atual)" : ""} • Total de Parlamentares: ${totalParlamentares}`}
              style={{ width: "100%" }}
            />
          </div>

          {loading ? (
            <div className="loading-container">
              <ProgressSpinner />
            </div>
          ) : (
            <DataTable
              value={parlamentares}
              dataKey="id"
              style={{ width: "100%" }}
              paginator
              rows={10}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="{first} a {last} de {totalRecords}"
              emptyMessage={emptyMessage}
              showGridlines
              stripedRows
            >
              <Column
                field="nome"
                header="Parlamentar"
                body={nomeBody}
                sortable
                style={{ minWidth: "180px" }}
              />
              <Column
                field="partido"
                header="Partido"
                body={partidoBody}
                sortable
                style={{ minWidth: "50px" }}
              />
              <Column
                header="Legislatura"
                body={legislaturaBody}
                sortable
                style={{ minWidth: "180px" }}
              />
              <Column
                header="Status"
                body={statusBody}
                style={{ width: "120px" }}
              />
              <Column
                header="Ativo"
                body={ativoBody}
                style={{ width: "120px" }}
              />
              <Column
                header="Ações"
                body={acoesBody}
                style={{ width: "120px" }}
              />
            </DataTable>
          )}
        </Card>
      </div>

      {/* Modal de Criar/Editar Parlamentar Moderno */}
      <Dialog
        visible={modalVisible}
        style={{ width: "750px", maxWidth: "90vw" }}
        modal
        className="p-fluid modern-dialog"
        onHide={() => setModalVisible(false)}
        footer={modalFooter}
        header={
          <div className="flex align-items-center gap-2">
            <div
              className="bg-primary bg-opacity-10 border-circle p-2"
              style={{ borderRadius: "12px" }}
            >
              <i
                className={`pi ${editandoParlamentar ? "pi-user-edit" : "pi-user-plus"} text-primary text-xl`}
              ></i>
            </div>
            <span className="font-bold text-xl">
              {editandoParlamentar ? "Editar Parlamentar" : "Novo Parlamentar"}
            </span>
          </div>
        }
      >
        <div className="modal-form" style={{ padding: 0 }}>
          <div
            className="grid p-fluid"
            style={{
              backgroundColor: "#fafafa",
              border: "1px solid #e8e8e8",
              padding: "15px", // Aumentei o padding para não cortar a borda
              borderRadius: "12px", // Aumentei para ficar mais moderno
              boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
              margin: 0, // Remove margem externa
            }}
          >
            <div className="col-12">
              <div className="field" style={{ marginBottom: "5px" }}>
                {" "}
                {/* Reduzido */}
                <label className="font-semibold mb-1">Legislatura </label>
                <Dropdown
                  value={formData.legislatura_id}
                  options={legislaturas.map((l) => ({
                    label: l.name,
                    value: l.id,
                  }))}
                  onChange={(e) => {
                    const legSelecionada = legislaturas.find(
                      (l) => l.id === e.value,
                    );
                    setFormData({
                      ...formData,
                      legislatura_id: e.value,
                      data_inicio_mandato: legSelecionada?.data_inicio
                        ? new Date(legSelecionada.data_inicio)
                        : formData.data_inicio_mandato,
                      data_fim_mandato: legSelecionada?.data_fim
                        ? new Date(legSelecionada.data_fim)
                        : formData.data_fim_mandato,
                    });
                  }}
                  placeholder="Selecione a legislatura"
                  showClear
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "5px" }}>
                {" "}
                {/* Reduzido */}
                <label className="font-semibold mb-1">Nome Completo </label>
                <InputText
                  value={formData.nome}
                  onChange={(e) =>
                    setFormData({ ...formData, nome: e.target.value })
                  }
                  placeholder="Nome completo do parlamentar"
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "5px" }}>
                {" "}
                {/* Reduzido */}
                <label className="font-semibold mb-1">Nome de Urna</label>
                <InputText
                  value={formData.nome_parlamentar}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      nome_parlamentar: e.target.value,
                    })
                  }
                  placeholder="Nome que aparece nas votações"
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "5px" }}>
                {" "}
                {/* Reduzido */}
                <label className="font-semibold mb-1">CPF </label>
                <InputMask
                  value={formData.cpf}
                  onChange={(e) =>
                    setFormData({ ...formData, cpf: e.target.value })
                  }
                  mask="999.999.999-99"
                  placeholder="999.999.999-99"
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "5px" }}>
                {" "}
                {/* Reduzido */}
                <label className="font-semibold mb-1">Partido</label>
                <InputText
                  value={formData.partido}
                  onChange={(e) =>
                    setFormData({ ...formData, partido: e.target.value })
                  }
                  placeholder="Partido político"
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "5px" }}>
                {" "}
                {/* Reduzido */}
                <label className="font-semibold mb-1">E-mail</label>
                <InputText
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="email@exemplo.com"
                  type="email"
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "5px" }}>
                {" "}
                {/* Reduzido */}
                <label className="font-semibold mb-1">Telefone</label>
                <InputMask
                  value={formData.telefone}
                  onChange={(e) =>
                    setFormData({ ...formData, telefone: e.target.value })
                  }
                  mask="(99) 99999-9999"
                  placeholder="(99) 99999-9999"
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "5px" }}>
                {" "}
                {/* Reduzido */}
                <label className="font-semibold mb-1">Início do Mandato</label>
                <Calendar
                  value={formData.data_inicio_mandato}
                  onChange={(e) =>
                    setFormData({ ...formData, data_inicio_mandato: e.value })
                  }
                  dateFormat="dd/mm/yy"
                  placeholder="dd/mm/aaaa"
                  showIcon
                  showButtonBar
                />
              </div>
            </div>

            <div className="col-12 md:col-6">
              <div className="field" style={{ marginBottom: "5px" }}>
                {" "}
                {/* Reduzido */}
                <label className="font-semibold mb-1">Fim do Mandato</label>
                <Calendar
                  value={formData.data_fim_mandato}
                  onChange={(e) =>
                    setFormData({ ...formData, data_fim_mandato: e.value })
                  }
                  dateFormat="dd/mm/yy"
                  placeholder="dd/mm/aaaa"
                  showIcon
                  showButtonBar
                />
              </div>
            </div>

            <div className="col-12">
              <div className="field" style={{ marginBottom: 0 }}>
                {" "}
                {/* Último sem margem */}
                <div className="flex gap-4 flex-wrap">
                  <div className="flex align-items-center gap-2">
                    <Checkbox
                      inputId="ativo"
                      checked={formData.ativo}
                      onChange={(e) =>
                        setFormData({ ...formData, ativo: e.checked })
                      }
                    />
                    <label htmlFor="ativo" className="font-semibold">
                      Parlamentar Ativo
                    </label>
                  </div>
                  <div className="flex align-items-center gap-2">
                    <Checkbox
                      inputId="titular"
                      checked={formData.titular}
                      onChange={(e) =>
                        setFormData({ ...formData, titular: e.checked })
                      }
                    />
                    <label htmlFor="titular" className="font-semibold">
                      Parlamentar Titular
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Parlamentares;
